/**
 * Presentation for a referral's qualification checklist, shared by the
 * affiliate portal and the control panel.
 *
 * The numbers are rendered exactly as the API reported them: the live metric
 * over the threshold the active rule carries, with no comparison of the two.
 * Pass/fail is `criterion.met`, which comes from the server's
 * `criteriaMetMask`, because a referral was graded under the rule in force at
 * the time while the thresholds on the row are the ones in force now —
 * deriving the verdict here would contradict the API the moment the rule moves.
 *
 * `keyPrefix` is passed in rather than hard-coded: the two surfaces keep their
 * copy in different i18n namespaces, and this module owns the shape of the
 * label, not where its words live.
 */
import type { AffiliateReferralCriterion } from '../api/affiliate/affiliateTypes';

type TranslateProgress = (
  key: string,
  options?: Record<string, unknown>,
) => string;

/** Criteria measured in days, so the label can carry the unit. */
const DAY_CRITERIA: ReadonlySet<AffiliateReferralCriterion['id']> = new Set([
  'tenure',
  'activityDistinctDays',
  'activeLoginDays',
]);

/**
 * The right-hand side of one checklist row: "6 / 14 days", "2 / 2", "Met".
 *
 * A criterion the active rule does not gate on says so rather than showing a
 * threshold of 0 that any value clears — "0 / 0" reads as a gate that was
 * passed instead of one that was never applied.
 */
export const affiliateCriterionProgressLabel = (
  criterion: AffiliateReferralCriterion,
  t: TranslateProgress,
  keyPrefix: string,
): string => {
  if (!criterion.required) {
    return t(`${keyPrefix}.notRequired`);
  }
  if (criterion.value === null || criterion.threshold === null) {
    return t(
      criterion.met
        ? `${keyPrefix}.progressFlagMet`
        : `${keyPrefix}.progressFlagUnmet`,
    );
  }
  return t(
    DAY_CRITERIA.has(criterion.id)
      ? `${keyPrefix}.progressDays`
      : `${keyPrefix}.progressCount`,
    { value: criterion.value, threshold: criterion.threshold },
  );
};

/** Only the gates the active rule enforces, for a compact progress cell. */
export const requiredAffiliateCriteria = (
  criteria: readonly AffiliateReferralCriterion[],
): readonly AffiliateReferralCriterion[] =>
  criteria.filter(criterion => criterion.required);
